/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose, lifecycle, setDisplayName, withState, withStateHandlers } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Notification } from '@coursera/coursera-ui';

import { toProgramHome } from 'bundles/enterprise-legacy-learner-home/links';
import storageUtils from 'bundles/enterprise-legacy-learner-home/utils/storageUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const styles = {
  trackedLink: css`
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }

    &:focus {
      text-decoration: underline;
    }
  `,
};

// bundles/naptimejs/resources/enterprisePrograms.v1 is untyped, so use this nifty stub.
type EnterpriseProgramsV1 = {
  id: string;
  name: string;
  slug: string;
};

type PropsFromCaller = {
  userId: number;
  program: EnterpriseProgramsV1;
  pathname?: string;
  addParentHeightClass: (className: string) => void;
  removeParentHeightClass: (className: string) => void;
};

type PropsFromWithState = {
  isMounted: boolean;
  setIsMounted: (isMounted: boolean) => void;
};

type StateFromWithStateHandler = {
  dismissed: boolean;
};

type HandlersFromWithStateHandler = {
  dismiss: () => StateFromWithStateHandler;
};

type PropsFromWithStateHandler = StateFromWithStateHandler & HandlersFromWithStateHandler;

type Props = PropsFromCaller & PropsFromWithState & PropsFromWithStateHandler;

const PAGES = {
  DEGREES: 'degrees',
  CERTIFICATES: 'certificates',
};

export function EnterpriseReturnToProgramBannerRenderer({
  program: { id, name, slug },
  pathname,
  isMounted,
  dismissed,
  dismiss,
}: Props) {
  if (dismissed) {
    return null;
  }

  const links = {
    returnLink: (
      <TrackedA
        href={toProgramHome(slug)}
        trackingName="enterprise_return_to_program_banner_return_to_program_link"
        data={{ program: { id, name, slug } }}
        css={styles.trackedLink}
      >
        {_t('Return to your learning program.')}
      </TrackedA>
    ),
    financialResourcesLink: (
      <TrackedA
        href="/degrees/coursera-financial-resources"
        trackingName="enterprise_return_to_program_banner_visit_financial_resources_link"
        data={{ program: { id, name, slug } }}
        css={styles.trackedLink}
      >
        {_t('visit our financial resources page.')}
      </TrackedA>
    ),
  };

  const messages = {
    degreesMessage: (
      <FormattedMessage
        message={_t(
          `Welcome to degrees on Coursera. This program type is not covered by your current learning plan. If you'd like to explore options for financing a degree, including employer tuition reimbursement, {visitFinancialResourcesLink}`
        )}
        visitFinancialResourcesLink={links.financialResourcesLink}
      />
    ),
    certificatesMessage: (
      <FormattedMessage
        message={_t(
          `Certificate programs aren't included in your learning plan. You may need to pay separately. {returnToYourLearningProgramLink}`
        )}
        returnToYourLearningProgramLink={links.returnLink}
      />
    ),
    defaultMessage: (
      <FormattedMessage
        message={_t(
          'You have navigated to My Coursera. You will be required to pay out of pocket to enroll on this page. {returnToYourLearningProgramLink}'
        )}
        returnToYourLearningProgramLink={links.returnLink}
      />
    ),
  };

  const getMessageToDisplay = () => {
    switch (pathname?.split('/')?.[1]) {
      case PAGES.DEGREES:
        return messages.degreesMessage;
      case PAGES.CERTIFICATES:
        return messages.certificatesMessage;
      default:
        return messages.defaultMessage;
    }
  };

  const messageToDisplay = getMessageToDisplay();

  return (
    <TrackedDiv
      className="rc-EnterpriseReturnToProgramBanner"
      trackingName="enterprise_return_to_program_banner"
      role="alert"
    >
      {isMounted && <Notification type="info" message={messageToDisplay} isDismissible onDismiss={dismiss} />}
    </TrackedDiv>
  );
}

export const enhance = compose<Props, PropsFromCaller>(
  setDisplayName('EnterpriseReturnToProgramBanner'),
  withState('isMounted', 'setIsMounted', false),
  withStateHandlers<StateFromWithStateHandler, HandlersFromWithStateHandler, PropsFromCaller>(
    ({ userId, program: { id: programId } }) => ({
      dismissed: storageUtils.get(userId, programId, 'EnterpriseReturnToProgramBannerDismissed') ?? false,
    }),
    {
      dismiss:
        (_, { userId, program: { id: programId }, removeParentHeightClass }) =>
        () => {
          storageUtils.set(userId, programId, 'EnterpriseReturnToProgramBannerDismissed', true);
          removeParentHeightClass('with-enterprise-return-to-program-banner');
          return { dismissed: true };
        },
    }
  ),
  lifecycle<Props, {}>({
    componentDidMount() {
      const { setIsMounted, dismissed, addParentHeightClass } = this.props;
      setIsMounted(true);
      if (!dismissed) {
        addParentHeightClass('with-enterprise-return-to-program-banner');
      }
    },
  })
);

export default enhance(EnterpriseReturnToProgramBannerRenderer);
